/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { IAssetArea, IAssetDto, IAssetUpdateRequestDto } from '../Asset.model'
import { useAppSelector } from '../../store/Store'
import { selectAssetFamilies, selectAssetAreas } from '../Asset.slice'
import {
  useAssetGetByIdMutation,
  useAssetAreaGetAllMutation,
  useAssetUpdateByIdMutation,
  useAssetFamilyGetAllMutation,
} from '../Asset.api'
import { FormikProvider, useFormik } from 'formik'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { object, string } from 'yup'
import { IContractDto, IContractFilter, IContractStatus } from '../../contract/Contract.model'
import { useContractGetByIdMutation, useContractSearchMutation } from '../../contract/Contract.api'
import { ContractListItem } from '../../contract/components/ContractListItem'
import { IFilterDto } from '../../../common/model/Core.model'
import classNames from 'classnames'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IMetadataSection } from '../../report-metadata/ReportMetadata.model'
import { IAssetMetadataDto } from '../../asset-metadata/AssetMetadata.model'
import { useAssetMetadataGetByAreaMutation } from '../../asset-metadata/AssetMetadata.api'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { COUNTRIES_EN, renderIdentifier } from '../../../util/Constants'
import { preventEnterSubmit } from '../../../util/Functions'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import ReportMetadataFormValidator from '../../report-metadata/ReportMetadataFormValidator.util'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { IClientDto } from '../../client/Client.model'
import { useClientGetByIdMutation } from '../../client/Client.api'

export interface IUpdateFormikSchema {
  assetMetadataId?: string
  contractId?: string
  id: string
  familyCode?: string
  family?: string
  areaCode?: string
  area?: string
  brand?: string
  model?: string
  serial?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  metadata?: IMetadataSection
}

const AssetEditPage: FC = () => {
    const intl = useIntl()
    const metadataFormValidator = new ReportMetadataFormValidator(intl);
    const navigate = useNavigate()
    const {assetId} = useParams()
    const breadCrumbs: Array<PageLink> = [
      {
        title: intl.formatMessage({id: 'BREADCRUMBS.ASSETS'}),
        path: '/assets',
        isSeparator: false,
        isActive: false,
      },
    ]

    const [assetAreaResult, setAssetAreaResult] = useState<IAssetArea[] | undefined>(undefined)
    const [loadError, setLoadError] = useState<string | undefined>(undefined)
    const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
    const [validationSchema, setValidationSchema] = useState<any>(
      object().shape({
        contractId: string().notRequired(),
        id: string().required(),
        family: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
        area: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
        brand: string()
          .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        model: string()
          .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        serial: string()
          .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        countryCode: string()
          .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        address: string()
          .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        latitude: string()
          .notRequired()
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        longitude: string()
          .notRequired()
          .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
        metadata: metadataFormValidator.validateStrict(),
      })
    )
    const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
      contractId: '',
      id: '',
      familyCode: '',
      family: '',
      areaCode: '',
      area: '',
      brand: '',
      model: '',
      serial: '',
      countryCode: '',
      country: '',
      address: '',
      latitude: '',
      longitude: '',
      metadata: undefined,
    })

    const didFetchAsset = useRef(false)
    const [isAssetLoading, setIsAssetLoading] = useState(true)
    const [asset, setAsset] = useState<IAssetDto>()
    const [assetGetById] = useAssetGetByIdMutation()
    const [assetUpdateById] = useAssetUpdateByIdMutation()

    const didFetchClient = useRef(false)
    const [client, setClient] = useState<IClientDto | undefined>()
    const [isClientLoading, setIsClientLoading] = useState(false)
    const [clientGetById] = useClientGetByIdMutation()

    const contractFilterKeys = {
      defaultKey: IContractFilter.ID,
      defaultKeyList: [
        IContractFilter.ID,
        IContractFilter.DOCUMENT,
        IContractFilter.SERVICE,
        IContractFilter.FREQUENCY,
      ],
    }
    const [contractFilterSuggestions, setContractFilterSuggestions] = useState<string[]>(contractFilterKeys.defaultKeyList)
    const [contractFilters, setContractFilters] = useState<IFilterDto[]>([])
    const didFetchContract = useRef(false)
    const [isContractLoading, setIsContractLoading] = useState(false)
    const [contract, setContract] = useState<IContractDto>()
    const [contractSearchResult, setContractSearchResult] = useState<IContractDto[] | undefined>(undefined)
    const [contractGetById] = useContractGetByIdMutation()
    const [contractSearch] = useContractSearchMutation()

    const assetFamilies = useAppSelector(selectAssetFamilies)
    const assetAreas = useAppSelector(selectAssetAreas)
    const [assetFamiliesGetAll] = useAssetFamilyGetAllMutation()
    const [assetAreasGetAll] = useAssetAreaGetAllMutation()

    const didFetchAssetMetadata = useRef(false)
    const [assetMetadata, setAssetMetadata] = useState<IAssetMetadataDto | undefined>(undefined)
    const [isAssetMetadataLoading, setIsAssetMetadataLoading] = useState(false)
    const [assetMetadataGetByArea] = useAssetMetadataGetByAreaMutation()

    const formik = useFormik<IUpdateFormikSchema>({
      enableReinitialize: true,
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: async (values, {setStatus, setSubmitting}) => {
        try {
          if (assetId && values) {
            setSubmitting(true)

            const request: IAssetUpdateRequestDto = {
              assetMetadataId: values.assetMetadataId,
              contractId: values.contractId,
              id: values.id,
              familyCode: values.familyCode,
              family: values.family,
              areaCode: values.areaCode,
              area: values.area,
              brand: values.brand,
              model: values.model,
              serial: values.serial,
              countryCode: values.countryCode,
              country: intl.formatMessage({id: `DROPDOWN.COUNTRY.${values.countryCode}`}),
              address: values.address,
              latitude: values.latitude,
              longitude: values.longitude,
              metadata: values.metadata,
            }

            const response: any = await assetUpdateById(request).unwrap()
            if (response && response.error) {
              setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
            } else {
              navigate(`/assets/${response.id}`)
            }
          }
        } catch (e: any) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } finally {
          setSubmitting(false)
        }
      },
    })

    useEffect(() => {
      const handleEsc = (event) => {
        if (event.key === 'Escape') {
          setContractSearchResult(undefined)
        }
      }
      window.addEventListener('keydown', handleEsc)

      return () => {
        window.removeEventListener('keydown', handleEsc)
      }
    }, [])

    useEffect(() => {
      if (toastEntry) {
        const [messageType] = toastEntry
        const toastElement = document.getElementById('#CustomToast')
        const delay =
          messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
        if (toastElement) {
          toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
          const toast = new Toast(toastElement, {autohide: true, delay})
          toast.show()
        }
      }
    }, [toastEntry])

    useEffect(() => {
      try {
        if (!assetFamilies || assetFamilies.length === 0) {
          assetFamiliesGetAll()
        }
        if (!assetAreas || assetAreas.length === 0) {
          assetAreasGetAll()
        }

        const fetchAsset = async (assetId) => {
          try {
            if (!didFetchAsset.current) {
              setIsAssetLoading(true)
              const response: any = await assetGetById({id: assetId})
              if (!response || response.error) {
                setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
              } else {
                const asset: IAssetDto = response.data
                setAsset(asset)

                formik.setFieldValue('id', asset.id)
                formik.setFieldValue('contractId', asset.contract?.id)
                formik.setFieldValue('familyCode', asset.familyCode)
                formik.setFieldValue('family', asset.family)
                formik.setFieldValue('areaCode', asset.areaCode)
                formik.setFieldValue('area', asset.area)
                formik.setFieldValue('brand', asset.brand)
                formik.setFieldValue('model', asset.model)
                formik.setFieldValue('serial', asset.serial)
                formik.setFieldValue('countryCode', asset.countryCode)
                formik.setFieldValue('address', asset.address)
                formik.setFieldValue('latitude', asset.latitude)
                formik.setFieldValue('longitude', asset.longitude)
                formik.setFieldValue('metadata', asset.metadata)

                setAssetAreaResult(assetAreas.filter((assetArea) => assetArea.family === asset.family))
                setContractFilters([
                  {id: IContractFilter.CLIENT_HASH, value: asset.client.id, editable: false},
                ])
                if (asset.contract) {
                  fetchContract(asset.contract.id)
                } 
              }
            }
          } catch (e: any) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } finally {
            setIsAssetLoading(false)
          }
        }
       
        const fetchContract = async (contractId) => {
          try {
            if (!didFetchContract.current) {
              setIsContractLoading(true)
              const response: any = await contractGetById({id: contractId, fields: 'LIMITED'})
              if (!response || response.error) {
                setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
              } else {
                const contract: IContractDto = response.data
                setContract(contract)
              }
            }
          } catch (e: any) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } finally {
            setIsContractLoading(false)
          }
        }

        if (assetId) {
          fetchAsset(assetId)
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
      }
    }, [])

    const fetchAssetMedatada = async (familyCode, areaCode) => {
      try {
        if (!didFetchAssetMetadata.current) {
          setIsAssetMetadataLoading(true)
          const response: any = await assetMetadataGetByArea({familyCode, areaCode})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const assetMetadata: IAssetMetadataDto = response.data
            setAssetMetadata(assetMetadata)

            formik.setFieldValue('assetMetadataId', assetMetadata.id)
            formik.setFieldValue('metadata', assetMetadata.metadata)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAssetMetadataLoading(false)
      }
    }

    const familyOnChangeHandler = async (family) => {
      const areas = assetAreas.filter((assetArea) => assetArea.family === family)
      if (areas && areas.length > 0) {
        setAssetAreaResult(areas)
        formik.setFieldValue('familyCode', areas[0].familyCode)
        formik.setFieldValue('family', areas[0].family)
        formik.setFieldValue('area', '')
      } else {
        setAssetAreaResult(undefined)
        formik.setFieldValue('family', '')
        formik.setFieldValue('area', '')
        formik.setFieldValue('assetMetadataId', '')
        formik.setFieldValue('metadata', '')
      }
    }

    const areaOnChangeHandler = async (area) => {
      const assetArea = assetAreaResult?.find((assetArea) => assetArea.area === area)
      if (assetArea) {
        formik.setFieldValue('areaCode', assetArea.areaCode)
        formik.setFieldValue('area', assetArea.area)
        await fetchAssetMedatada(formik.values.familyCode, assetArea.areaCode)
      } else {
        formik.setFieldValue('area', '')
        formik.setFieldValue('assetMetadataId', '')
        formik.setFieldValue('metadata', '')
      }
    }

    const contractRowOnClickHandler = async (contractId) => {
      if (contractSearchResult) {
        const contract = contractSearchResult.find((contract) => contract.id === contractId)
        if (contract) {
          setContract(contract)
          setContractSearchResult(undefined)
          setContractFilters([])
          setContractFilterSuggestions(contractFilterKeys.defaultKeyList)

          formik.setFieldValue('contractId', contract.id)
          formik.setFieldValue('contractSequence', contract.sequence)
        }
      }
    }

    const contractRemoveOnClickHandler = async () => {
      setContract(undefined)
      setContractSearchResult(undefined)

      if (asset?.client) {
        setContractFilters([
          {id: IContractFilter.CLIENT_HASH, value: asset.client.id, editable: false},
          {id: IContractFilter.STATUS, value: IContractStatus.ACTIVE, editable: false},
        ])
        setContractFilterSuggestions(contractFilterKeys.defaultKeyList)
      } 

      formik.setFieldValue('contractId', '')
      formik.setFieldValue('contractSequence', '')
    }

    const contractSearchOnClickHandler = async () => {
      try {
        if (!didFetchContract.current) {
          setIsContractLoading(true)
          const response: any = await contractSearch({
            filters: contractFilters,
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            setContractSearchResult(response.data.data)
            setContractFilters(response.data.filters)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsContractLoading(false)
      }
    }

    const contractResetSearchResetOnClickHandler = async () => {
      setContractSearchResult(undefined)
      setContractFilterSuggestions(contractFilterKeys.defaultKeyList)
    }

    const tabWithError = (tabId) => {
      switch (tabId) {
        case 'kt_tab_pane_1': {
          return (
            (formik.touched.id && formik.errors.id) ||
            (formik.touched.family && formik.errors.family) ||
            (formik.touched.area && formik.errors.area) ||
            (formik.touched.brand && formik.errors.brand) ||
            (formik.touched.model && formik.errors.model) ||
            (formik.touched.serial && formik.errors.serial) ||
            (formik.touched.countryCode && formik.errors.countryCode) ||
            (formik.touched.address && formik.errors.address) ||
            (formik.touched.latitude && formik.errors.latitude) ||
            (formik.touched.longitude && formik.errors.longitude)
          )
        }
        case 'kt_tab_pane_2': {
          return formik.touched.metadata && formik.errors.metadata
        }
        default:
          return false
      }
    }
    
    return (
      <>
        {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
        <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
        <div className='card card-flush card-stretch-100 card-bordered mt-5'>
          <div className='card-body'>
            {loadError && (
              <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
                <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                  <i className='bi bi-database-exclamation fs-2'></i>
                </span>

                <div className='d-flex flex-column'>
                  <span>{loadError}</span>
                </div>

                <button
                  type='button'
                  className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                  data-bs-dismiss='alert'
                >
                  <div className='badge px-3 py-2'>
                    <span className='text-danger fs-1'>&times;</span>
                  </div>
                </button>
              </div>
            )}

            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
                <div className='d-flex flex-row'>
                  <div className='d-flex flex-column flex-row-fluid'>
                    <div className='d-flex flex-row flex-column-fluid'>
                      <div className='d-flex flex-row-fluid flex-start'>
                        {asset && (
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fs-2 fw-bolder'>
                              {asset.displayName}
                            </span>
                            <span className='fw-bolder text-primary me-1 fs-5'>
                              {renderIdentifier(asset.sequence)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className='d-flex flex-row-auto flex-start ms-5'>
                        {asset && (
                          <>
                            <button
                              type='submit'
                              className='btn btn-sm btn-primary me-2 d-none d-md-block'
                              onClick={() => formik.handleSubmit()}
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting ? (
                                <span className='indicator-progress' style={{display: 'block'}}>
                                  {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              ) : (
                                <>
                                  <i className='bi bi-floppy'></i>
                                  <span className='indicator-label'>
                                    {intl.formatMessage({id: 'UTIL.SAVE'})}
                                  </span>
                                </>
                              )}
                            </button>
                            <button
                              type='submit'
                              className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                              onClick={() => formik.handleSubmit()}
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting ? (
                                <span className='indicator-progress' style={{display: 'block'}}>
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              ) : (
                                <i className='bi bi-floppy'></i>
                              )}
                            </button>
                          </>
                        )}

                        {!formik.isSubmitting && (
                          <button
                            className='btn btn-sm btn-light'
                            onClick={() => navigate(`/assets/${assetId}`)}
                          >
                            <i className='bi bi-x-lg'></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {isAssetLoading && !formik.isSubmitting ? (
                  <Spinner />
                ) : !asset ? (
                  <div className='row'>
                    <div className='d-flex flex-row-fluid flex-start mt-10'>
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-6 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                          </span>
                        </div>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                      <li className='nav-item'>
                        <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                          {tabWithError('kt_tab_pane_1') && (
                            <span className='text-danger me-1'>*</span>
                          )}
                          {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                        </a>
                      </li>
                      {asset.metadata && asset.metadata.items && (
                        <li className='nav-item'>
                          <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                            {tabWithError('kt_tab_pane_2') && (
                              <span className='text-danger me-1'>*</span>
                            )}
                            {intl.formatMessage({id: 'UTIL.DETAILS'})}
                          </a>
                        </li>
                      )}
                    </ul>
                    <div className='tab-content mt-10' id='myTabContent'>
                      <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                        <div className='row mb-5'>
                          <div className='px-5'>
                            <ClientEmbedered client={asset.client} />
                          </div>
                        </div>

                        <div className='row mb-5'>
                          {!contract && (
                            <div className='px-5'>
                              <div className='row mb-5'>
                                <div className='row mb-2'>
                                  <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                    <span
                                      className={classNames({
                                        'text-gray-700': !(
                                          formik?.touched?.contractId && formik?.errors?.contractId
                                        ),
                                        'text-danger':
                                          formik?.touched?.contractId && formik?.errors?.contractId,
                                      })}
                                    >
                                      {intl.formatMessage({id: 'UTIL.CONTRACT'})}
                                    </span>
                                  </label>

                                  <div className='col-xxl-9'>
                                    <TagInputSearchListControls
                                      loading={isContractLoading}
                                      enabled={!formik.isSubmitting}
                                      filterKeys={contractFilterKeys}
                                      filterSuggestions={contractFilterSuggestions}
                                      filters={contractFilters}
                                      createOnClickPath={`/contracts/new?clientId=${asset.client.id}`}
                                      setFilterSuggestions={setContractFilterSuggestions}
                                      setFilters={setContractFilters}
                                      searchOnClickHandler={contractSearchOnClickHandler}
                                      resetSearchResetOnClickHandler={
                                        contractResetSearchResetOnClickHandler
                                      }
                                    ></TagInputSearchListControls>
                                  </div>
                                </div>

                                <div className='row mb-2'>
                                  <label className='col-xxl-3 text-gray-700 col-form-label'></label>
                                  <div className='col-xxl-9 h-5px'>
                                    {contractSearchResult && contractSearchResult.length > 0 && (
                                      <ul
                                        id='autocomplete-list'
                                        className='suggestions-list'
                                        role='listbox'
                                      >
                                        {contractSearchResult.map((contract, index) => (
                                          <li
                                            key={index}
                                            onClick={() => contractRowOnClickHandler(contract.id)}
                                          >
                                            <ContractListItem
                                              data={contract}
                                              rowSelected={contract.id}
                                              backgroundClass={
                                                contract.status === IContractStatus.EXPIRED
                                                  ? 'bg-light-danger'
                                                  : 'bg-light-primary'
                                              }
                                            ></ContractListItem>
                                            <div className='separator'></div>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {contract && (
                            <div className='px-5'>
                              <div className='row mb-5'>
                                <div className='row mb-2'>
                                  <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                    <span className='text-gray-700'>
                                      {intl.formatMessage({id: 'UTIL.CONTRACT'})}
                                    </span>
                                  </label>

                                  <div className='col-xxl-9 h-5px'>
                                    <ContractListItem
                                      disabled={formik.isSubmitting}
                                      data={contract}
                                      rowSelected={contract.id}
                                      removeOnClick={contractRemoveOnClickHandler}
                                      backgroundClass={
                                        contract.status === IContractStatus.EXPIRED
                                          ? 'bg-light-danger'
                                          : 'bg-light-primary'
                                      }
                                    ></ContractListItem>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                          />

                          <div className='px-5'>
                            <div className='row mb-5'>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                                value={asset?.displayName}
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.GUI'})}
                                value={asset?.gui}
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSET_FAMILY'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='family'
                                object={
                                  <select
                                    className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                    {...formik.getFieldProps('family')}
                                    onChange={(e) => {
                                      formik.handleChange(e)
                                      familyOnChangeHandler(e.target.value)
                                    }}
                                    disabled={formik.isSubmitting}
                                  >
                                    <option value=''>
                                      {intl.formatMessage({
                                        id: 'ASSET.FAMILY.DROPDOWN.DEFAULT',
                                      })}
                                    </option>
                                    {assetFamilies &&
                                      assetFamilies.map((family, index) => (
                                        <option key={index} value={family.family}>
                                          {family.family}
                                        </option>
                                      ))}
                                  </select>
                                }
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSET_AREA'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='area'
                                object={
                                  <select
                                    className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                    {...formik.getFieldProps('area')}
                                    onChange={(e) => {
                                      formik.handleChange(e)
                                      areaOnChangeHandler(e.target.value)
                                    }}
                                    disabled={formik.isSubmitting}
                                  >
                                    <option value=''>
                                      {intl.formatMessage({
                                        id: 'ASSET.AREA.DROPDOWN.DEFAULT',
                                      })}
                                    </option>
                                    {assetAreaResult &&
                                      assetAreaResult?.map((assetArea, index) => (
                                        <option key={index} value={assetArea.area}>
                                          {assetArea.area}
                                        </option>
                                      ))}
                                  </select>
                                }
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSET_BRAND'})}
                                placeholder={intl.formatMessage({
                                  id: 'UTIL.ASSET_BRAND',
                                })}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='brand'
                                enableInputLength={true}
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSET_MODEL'})}
                                placeholder={intl.formatMessage({
                                  id: 'UTIL.ASSET_MODEL',
                                })}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='model'
                                enableInputLength={true}
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSET_SERIAL'})}
                                placeholder={intl.formatMessage({
                                  id: 'UTIL.ASSET_SERIAL',
                                })}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='serial'
                                enableInputLength={true}
                              ></CustomInputRenderer>
                            </div>
                          </div>
                        </div>
                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                          />

                          <div className='px-5'>
                            <div className='row mb-5'>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='countryCode'
                                object={
                                  <select
                                    className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                    {...formik.getFieldProps('countryCode')}
                                    onChange={formik.handleChange}
                                    disabled={formik.isSubmitting}
                                  >
                                    <option value=''>
                                      {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                    </option>
                                    {COUNTRIES_EN &&
                                      COUNTRIES_EN.map((country, index) => (
                                        <option key={index} value={country.code}>
                                          {intl.formatMessage({
                                            id: `DROPDOWN.COUNTRY.${country.code}`,
                                          })}
                                        </option>
                                      ))}
                                  </select>
                                }
                              ></CustomInputRenderer>

                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                                placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='address'
                                enableInputLength={true}
                              ></CustomInputRenderer>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                        <div className='row mb-5'>
                          {asset && asset.metadata && (
                            <ReportRenderer formik={formik} path={`metadata.items`} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </FormikProvider>
          </div>
        </div>
      </>
    )
}

export {AssetEditPage}
